import React from 'react';
import { C } from '../constants';
import {
  listarPedidosAdmin,
  requisitosDeFactura, emitirFacturaPedido, reciboCajaPedido, notaCreditoPedido,
  pdfDocumentoSiigo, estamparDocumentoSiigo, erroresDianSiigo,
} from '../services/pagos';

/**
 * SEGUIMIENTO DE PAGOS — la vista de flota que faltaba.
 *
 * Hasta acá el admin veía la parte fiscal de a UN pedido (`PedidoFiscal`: escribí el id, mirá su
 * factura). Lo que no existía es la lista de TODO lo que se cobró con su estado de pago Y su estado
 * fiscal en la misma fila: cuánto entró, por qué medio, si ya tiene factura DIAN, si esa factura
 * quedó estampada (CUFE) o rebotó, y si el recibo de caja salió. Sin esta tabla, «plata cobrada sin
 * documento» —una FV que rebotó en Siigo cuando entró el pago— no se ve hasta que alguien la busca.
 *
 * 🔌 SE ALIMENTA de `GET /pagos/pedidos` (admin). Si el backend todavía no expone esa ruta, la tabla
 * NO finge datos: lo dice y explica qué falta. Un panel que muestra una lista vacía como si fuera
 * «no hay pagos» sería el mismo «200 que miente» que ya mordió a la suite. El estado fiscal por fila
 * sale de las columnas `siigo_*` del propio pedido (número, CUFE, recibo, DS), que la cadena
 * automática ya escribe; no hay una segunda consulta a Siigo por fila.
 */

const CAJA = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' };
const TITULO = { fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 8px' };

const fmtCOP = (n) => (n != null && Number.isFinite(Number(n)) ? `$${Math.round(Number(n)).toLocaleString('es-CO')}` : '—');

/** Estado de PAGO del pedido (no el fiscal): cobrado, pendiente, anulado. */
function estadoPago(p) {
  const e = String(p.estado || '').toLowerCase();
  if (e === 'pagado' || e === 'aprobado') return { label: 'Pagado', col: C.teal };
  if (e === 'anulado' || e === 'rechazado' || e === 'fallido') return { label: e[0].toUpperCase() + e.slice(1), col: C.red };
  return { label: e || 'pendiente', col: C.amber };
}

/**
 * Estado FISCAL derivado de las columnas `siigo_*` del pedido. Cuatro niveles, del peor al mejor:
 *   sin factura → facturada sin estampar → estampada (CUFE) ; y el aviso de recibo/DS aparte.
 * El caso que importa vigilar es «Pagado» + «Sin factura»: eso es plata sin documento.
 */
function estadoFiscal(p) {
  if (!p.siigo_invoice_id && !p.siigo_numero) {
    const cobrado = ['pagado', 'aprobado'].includes(String(p.estado || '').toLowerCase());
    return { label: 'Sin factura', col: cobrado ? C.orange : C.muted, alerta: cobrado };
  }
  if (p.siigo_cufe) return { label: `FV ${p.siigo_numero || ''} · CUFE ✓`.trim(), col: C.teal, alerta: false };
  return { label: `FV ${p.siigo_numero || 'emitida'} · sin estampar`, col: C.amber, alerta: false };
}

function Badge({ label, col }) {
  return (
    <span style={{ fontSize: 9.5, fontWeight: 700, color: col, background: `${col}18`, border: `1px solid ${col}44`, borderRadius: 5, padding: '2px 8px', whiteSpace: 'nowrap' }}>{label}</span>
  );
}

export default function SeguimientoPagos() {
  const [estado, setEstado] = React.useState('cargando'); // 'cargando' | 'ok' | 'sin_endpoint' | 'error'
  const [pedidos, setPedidos] = React.useState([]);
  const [msg, setMsg] = React.useState('');
  const [filtro, setFiltro] = React.useState('cobrado'); // 'cobrado' | 'sin_documento' | 'todos'
  const [sel, setSel] = React.useState(null);

  const cargar = React.useCallback(async () => {
    setEstado('cargando');
    setMsg('');
    try {
      // Feature-detect: la ruta admin de flota puede no existir todavía en el backend desplegado.
      const r = await listarPedidosAdmin({ limite: 100 });
      if (r && r.ok && Array.isArray(r.pedidos)) {
        setPedidos(r.pedidos);
        setEstado('ok');
      } else if (r && r.migrado === false) {
        setEstado('sin_endpoint');
      } else {
        setEstado('error');
        setMsg(r?.error || 'El backend no devolvió la lista de pedidos.');
      }
    } catch (e) {
      // Un 404 de la ruta llega como error de red/HTTP; se lee como «endpoint pendiente», no como fallo.
      if (/404|no migrado|not found/i.test(e.message || '')) setEstado('sin_endpoint');
      else { setEstado('error'); setMsg(e.message); }
    }
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);

  const accion = async (fn, id, etiqueta, tipo) => {
    setMsg(`${etiqueta}…`);
    const r = await fn(id).catch((e) => ({ ok: false, error: e.message }));
    if (r?.ok) {
      setSel({ id, tipo, ...r });
      setMsg('');
      cargar(); // el estado fiscal de la fila cambió (nueva FV, estampado…): refrescar
    } else {
      setMsg(r?.error || 'Siigo respondió con un error.');
    }
  };

  const facturar = async (p) => {
    setMsg('Verificando requisitos…');
    const req = await requisitosDeFactura(p.id).catch((e) => ({ ok: false, error: e.message }));
    if (req && req.puede_facturar === false) {
      setMsg(`No se puede facturar todavía: falta ${(req.faltan || []).join(', ') || 'datos del cliente'}.`);
      return;
    }
    accion((id) => emitirFacturaPedido(id), p.id, 'Emitiendo factura DIAN', 'factura');
  };

  const visibles = pedidos.filter((p) => {
    if (filtro === 'todos') return true;
    const cobrado = ['pagado', 'aprobado'].includes(String(p.estado || '').toLowerCase());
    if (filtro === 'cobrado') return cobrado;
    if (filtro === 'sin_documento') return cobrado && !p.siigo_invoice_id && !p.siigo_numero;
    return true;
  });

  const sinDoc = pedidos.filter((p) => ['pagado', 'aprobado'].includes(String(p.estado || '').toLowerCase()) && !p.siigo_invoice_id && !p.siigo_numero).length;

  return (
    <div style={{ ...CAJA, borderLeft: `3px solid ${sinDoc ? C.orange : C.teal}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ ...TITULO, margin: 0 }}>Seguimiento de pagos</h3>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['cobrado', 'Cobrados'], ['sin_documento', 'Sin documento'], ['todos', 'Todos']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFiltro(k)}
              style={{ background: filtro === k ? C.teal : 'transparent', color: filtro === k ? C.heading : C.muted, border: `1px solid ${filtro === k ? C.teal : C.border}`, borderRadius: 8, padding: '5px 11px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              {label}{k === 'sin_documento' && sinDoc > 0 ? ` (${sinDoc})` : ''}
            </button>
          ))}
          <button type="button" onClick={cargar}
            style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 8, padding: '5px 11px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
            Actualizar
          </button>
        </div>
      </div>

      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
        Cada cobro con su estado de pago y su estado fiscal en la misma fila.
        {sinDoc > 0 && (
          <> <strong style={{ color: C.orange }}>Hay {sinDoc} cobrado{sinDoc === 1 ? '' : 's'} sin factura: eso es
          plata sin documento, hay que emitirla.</strong></>
        )}
      </div>

      {msg && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 8 }}>{msg}</div>}

      {estado === 'cargando' && <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>Consultando pedidos…</div>}

      {estado === 'sin_endpoint' && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, lineHeight: 1.55, background: `${C.amber}12`, border: `1px solid ${C.amber}33`, borderRadius: 8, padding: '10px 12px' }}>
          La lista de flota necesita la ruta <code>GET /pagos/pedidos</code> en el hub-api, que todavía no
          está desplegada. Mientras tanto, cada pedido se factura desde <strong style={{ color: C.heading }}>Pedido
          → documentos</strong> (abajo) escribiendo su id. En cuanto el backend exponga la ruta, esta tabla se
          llena sola.
        </div>
      )}

      {estado === 'error' && (
        <div style={{ fontSize: 11.5, color: C.red, marginTop: 10 }}>No se pudo leer la lista. {msg}</div>
      )}

      {estado === 'ok' && visibles.length === 0 && (
        <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>Nada que mostrar con este filtro.</div>
      )}

      {estado === 'ok' && visibles.length > 0 && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr>{['Pedido', 'Cliente', 'Total', 'Medio', 'Pago', 'Estado fiscal', 'Acciones'].map((t) => (
                <th key={t} style={{ textAlign: 'left', fontSize: 9.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, padding: '8px 6px', borderBottom: `1px solid ${C.border}` }}>{t}</th>
              ))}</tr>
            </thead>
            <tbody>
              {visibles.slice(0, 100).map((p) => {
                const ep = estadoPago(p);
                const ef = estadoFiscal(p);
                const facturado = Boolean(p.siigo_invoice_id || p.siigo_numero);
                return (
                  <tr key={p.id} style={ef.alerta ? { background: `${C.orange}0d` } : undefined}>
                    <td style={{ fontSize: 12, color: C.heading, fontWeight: 600, padding: '7px 6px', whiteSpace: 'nowrap' }}>{p.id}</td>
                    <td style={{ fontSize: 12, color: C.text, padding: '7px 6px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nombre || p.email || '—'}</td>
                    <td style={{ fontSize: 12, color: C.text, padding: '7px 6px', whiteSpace: 'nowrap' }}>{fmtCOP(p.total)}</td>
                    <td style={{ fontSize: 11.5, color: C.muted, padding: '7px 6px', whiteSpace: 'nowrap' }}>{p.medio || p.pasarela || '—'}</td>
                    <td style={{ padding: '7px 6px' }}><Badge label={ep.label} col={ep.col} /></td>
                    <td style={{ padding: '7px 6px' }}><Badge label={ef.label} col={ef.col} /></td>
                    <td style={{ padding: '7px 6px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {!facturado ? (
                          <button type="button" onClick={() => facturar(p)}
                            style={{ background: C.teal, color: C.heading, border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Facturar</button>
                        ) : (
                          <>
                            {p.siigo_pdf_url ? (
                              <a href={p.siigo_pdf_url} target="_blank" rel="noreferrer"
                                style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, textDecoration: 'none' }}>PDF</a>
                            ) : (
                              <button type="button" onClick={() => accion((id) => pdfDocumentoSiigo(id), p.siigo_invoice_id, 'Bajando PDF', 'pdf')}
                                style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>PDF</button>
                            )}
                            {!p.siigo_recibo_id && (
                              <button type="button" onClick={() => accion((id) => reciboCajaPedido(id), p.id, 'Recibo de caja', 'recibo')}
                                style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>Recibo</button>
                            )}
                            {!p.siigo_cufe && (
                              <button type="button" onClick={() => accion((id) => estamparDocumentoSiigo(id), p.siigo_invoice_id, 'Estampando', 'estampa')}
                                style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>Estampar</button>
                            )}
                            <button type="button" onClick={() => accion((id) => erroresDianSiigo(id), p.siigo_invoice_id, 'Consultando el rechazo', 'errores')}
                              style={{ background: 'transparent', color: C.orange, border: `1px solid ${C.orange}55`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>¿Falló?</button>
                            <button type="button" onClick={() => accion((id) => notaCreditoPedido(id), p.id, 'Nota crédito', 'nc')}
                              style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>NC</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <pre style={{ marginTop: 12, background: `${C.muted}12`, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 11, color: C.text, overflowX: 'auto', maxHeight: 240 }}>
          {JSON.stringify({ ...sel, base64: sel.base64 ? `${sel.base64.slice(0, 40)}… (${sel.base64.length} bytes)` : undefined, crudo: undefined }, null, 2)}
        </pre>
      )}
    </div>
  );
}
